/**
 * DataTypesManagement - Standalone Data Types Management Component
 *
 * Manages data types with CRUD operations using DataCard for display.
 */

import { useState, useEffect } from 'react';
import { Button, Table, Spinner } from 'react-bootstrap';
import { apiClient } from '@/services/apiClient';
import toast from 'react-hot-toast';
import DataCard from '@/components/common/DataCard';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import DataTypesModal from './modals/DataTypesModal';
import EntityCSVUploadButton from './EntityCSVUploadButton';
import type { DataType } from '@/types/associations';

export default function DataTypesManagement() {
  // Entity state
  const [dataTypes, setDataTypes] = useState<DataType[]>([]);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Modal state
  const [modalState, setModalState] = useState<{ show: boolean; editing: DataType | null }>({
    show: false,
    editing: null,
  });

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    show: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // Load data on mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getAdminDataTypes();
      if (!response.error && response.data) {
        setDataTypes(response.data as DataType[]);
      }
    } catch (error) {
      toast.error('Failed to load data types');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // CRUD handlers
  const handleSave = async (data: Partial<DataType>) => {
    if (modalState.editing) {
      const response = await apiClient.updateAdminDataType(modalState.editing.id, data);
      if (response.error) throw new Error(response.error);
      toast.success('Data type updated successfully');
    } else {
      if (!data.name) {
        throw new Error('Name is required');
      }
      const response = await apiClient.createAdminDataType({
        name: data.name,
        description: data.description,
        is_active: (data as { is_active?: boolean }).is_active ?? true,
      });
      if (response.error) throw new Error(response.error);
      toast.success('Data type created successfully');
    }
    await loadData();
  };

  const handleDelete = (id: number) => {
    const dataType = dataTypes.find((dt) => dt.id === id);
    setConfirmModal({
      show: true,
      title: 'Delete Data Type',
      message: `Are you sure you want to delete "${dataType?.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          const response = await apiClient.deleteAdminDataType(id);
          if (response.error) throw new Error(response.error);
          toast.success('Data type deleted successfully');
          setSelectedId(null);
          await loadData();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to delete data type');
        } finally {
          setConfirmModal((prev) => ({ ...prev, show: false }));
        }
      },
    });
  };

  // Table rendering
  const renderTable = (items: DataType[]) => (
    <Table size="sm" striped hover className="mb-0">
      <thead>
        <tr>
          <th>Name</th>
          <th>Description</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {items.map((dataType) => (
          <tr
            key={dataType.id}
            onClick={() => setSelectedId(selectedId === dataType.id ? null : dataType.id)}
            style={{
              cursor: 'pointer',
              backgroundColor: selectedId === dataType.id ? '#e7f3ff' : undefined,
            }}
          >
            <td><strong>{dataType.name}</strong></td>
            <td className="text-muted small">{dataType.description || '-'}</td>
            <td>
              <span className={`badge ${dataType.is_active !== false ? 'bg-success' : 'bg-secondary'}`}>
                {dataType.is_active !== false ? 'Active' : 'Inactive'}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" />
        <p className="mt-3">Loading data types...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Data Types Management</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            Manage data types for datasets
          </p>
        </div>
      </div>

      <DataCard
        cardKey="data-types"
        title="Data Types"
        badgeCount={dataTypes.length}
        data={dataTypes}
        itemsPerPage={10}
        searchPlaceholder="Search data types..."
        filterFunction={(item, searchTerm) => {
          if (!searchTerm.trim()) return true;
          const term = searchTerm.toLowerCase();
          return (
            item.name.toLowerCase().includes(term) ||
            (item.description || '').toLowerCase().includes(term)
          );
        }}
        headerActions={
          <>
            {selectedId !== null && (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  onClick={() => {
                    const dataType = dataTypes.find((dt) => dt.id === selectedId);
                    if (dataType) setModalState({ show: true, editing: dataType });
                  }}
                  className="me-2"
                >
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => {
                    if (selectedId !== null) handleDelete(selectedId);
                  }}
                  className="me-2"
                >
                  Delete
                </Button>
              </>
            )}
            <EntityCSVUploadButton
              entityType="data-types"
              onUploadComplete={loadData}
              size="sm"
              variant="outline-success"
              className="me-2"
            />
            <Button
              size="sm"
              variant="primary"
              onClick={() => setModalState({ show: true, editing: null })}
            >
              Create
            </Button>
          </>
        }
        renderTable={renderTable}
        onRefresh={loadData}
        maxHeight="600px"
        emptyMessage="No data types found. Create your first data type."
      />

      {/* Data Type Modal */}
      <DataTypesModal
        show={modalState.show}
        dataType={modalState.editing}
        onHide={() => setModalState({ show: false, editing: null })}
        onSave={handleSave}
        onRefresh={loadData}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal((prev) => ({ ...prev, show: false }))}
      />
    </div>
  );
}
